import { useState } from "react";
import { useNavigate } from "react-router-dom";
import sanitizeHtml from "sanitize-html";
import Modal from "@/components/shared-ui/Modal";
import { sanitizeHtmlOptions } from "@/utils/sanitize.utils";
import { hasSeenBannerModal, markBannerModalSeen } from "@/helpers/bannerModal";
import * as Styled from './styled';

type Props = {
  modal: {
    id: string;
    content: string;
    buttonText: string | null;
    buttonLink: string | null;
  };
};

// modalButtonLink is meant to be an internal path (e.g. "/docs"), but
// there's no CMS-side enforcement - fall back to a real navigation for an
// absolute URL rather than letting react-router's navigate() silently
// mishandle it.
const isAbsoluteUrl = (url: string) =>
  /^[a-z][a-z0-9+.-]*:\/\//i.test(url) || url.startsWith("//");

const AcknowledgementModal = ({ modal }: Props) => {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(() =>
    hasSeenBannerModal(modal.id),
  );

  const dismiss = () => {
    markBannerModalSeen(modal.id);
    setDismissed(true);
  };

  if (dismissed) {
    return null;
  }

  return (
    <Modal
      modalActive
      data-testid="acknowledgementModal"
      toggleModal={dismiss}
      cancelButton={
        !modal.buttonText || !modal.buttonLink
          ? { label: "Dismiss", onClick: dismiss }
          : undefined
      }
      actionButton={
        modal.buttonText && modal.buttonLink
          ? {
              label: modal.buttonText,
              onClick: () => {
                dismiss();
                const link = modal.buttonLink as string;
                if (isAbsoluteUrl(link)) {
                  window.open(link, "_blank", "noopener,noreferrer");
                } else {
                  navigate(link);
                }
              },
            }
          : undefined
      }
    >
      <Styled.ModalContent
        dangerouslySetInnerHTML={{
          __html: sanitizeHtml(modal.content, sanitizeHtmlOptions),
        }}
      />
    </Modal>
  );
};

export default AcknowledgementModal;
